import { useState } from 'react'
import { Alert, Button, Card, Descriptions, Skeleton, Space, Tabs, Tag, Tooltip, Typography } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { EmptyState } from '@/shared/ui/EmptyState'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { PageHeader } from '@/shared/ui/PageHeader'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { BankAccountsCard } from '@/features/hr/BankAccountsCard'
import { EmploymentAssignModal } from '@/features/hr/EmploymentAssignModal'
import { EmploymentHistory } from '@/features/hr/EmploymentHistory'
import { LeaveCard } from '@/features/hr/LeaveCard'
import { PayslipsCard } from '@/features/hr/PayslipsCard'
import { PersonAttachmentsCard } from '@/features/hr/PersonAttachmentsCard'
import { PersonFormModal } from '@/features/hr/PersonFormModal'
import { PersonTerminateModal } from '@/features/hr/PersonTerminateModal'
import { RatingCard } from '@/features/hr/RatingCard'
import { SalaryCard } from '@/features/hr/SalaryCard'
import { useEmployeeNodes } from '@/features/hr/useEmployeeNodes'
import {
  fetchEmploymentHistory,
  fetchJobTitles,
  fetchPerson,
  setPersonState,
  terminatePerson,
  type PersonState,
} from '@/features/hr/hrApi'

const STATE_COLOR: Record<PersonState, string> = {
  ACTIVE: 'green',
  SUSPENDED: 'gold',
  TERMINATED: 'default',
}

/**
 * Xodim kartochkasi — shaxsiy ma'lumot, joriy tayinlash va TARIX.
 *
 * <h2>⚠️ TARIX — SAHIFANING ASOSIY QISMI, "qo'shimcha" emas</h2>
 * `QARORLAR.md` §20.9-E: `platform.audit_log` "kim nima qildi" degan
 * jurnal va u <i>"2024-yil martda bu odam qaysi filialda ishlagan"</i>
 * degan savolga javob BERA OLMAYDI. Aynan shu sababdan
 * `hr.employment_history` alohida jadval sifatida ochilgan — va aynan
 * shu sababdan u bu yerda to'liq ko'rsatiladi.
 *
 * ⚠️ Tarixda tahrirlash/o'chirish tugmasi YO'Q — sabab
 * `EmploymentHistory` javadoc'ida.
 *
 * <h2>⚠️ UCHTA AMAL, UCHTA HUQUQ</h2>
 * <ul>
 *   <li>Tahrirlash va holat — `hr.person.write`;</li>
 *   <li>Tayinlash — `hr.employment.write` (ko'rinish chegarasini
 *       siljitadi);</li>
 *   <li>Tarixni O'QISH — `hr.person.read` (alohida kod ATAYLAB
 *       ochilmagan: "kartochka ochildi, lekin yarmi bo'sh" holatini
 *       yaratmaslik uchun — `EmploymentController` javadoc'i).</li>
 * </ul>
 *
 * <h2>⚠️ NATIJA UCH HOLATLI</h2>
 * Ishdan bo'shatish va holat o'zgarishi `OperationResult` qaytaradi.
 * `PARTIAL` — XATO EMAS, ogohlantirish, va `useOperationRunner` uni
 * baribir kesh yangilash bilan birga ko'rsatadi (`CLAUDE.md` §3.2,
 * 4-qoida).
 */
export function HrPersonDetailPage() {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const navigate = useNavigate()
  const { id = '' } = useParams<{ id: string }>()

  const [editOpen, setEditOpen] = useState(false)
  const [assignOpen, setAssignOpen] = useState(false)
  const [terminateOpen, setTerminateOpen] = useState(false)

  const nodeSource = useEmployeeNodes()
  const runner = useOperationRunner([queryKeys.hr.all])

  const person = useQuery({
    queryKey: queryKeys.hr.person(id),
    queryFn: () => fetchPerson(id),
    enabled: id !== '',
  })

  const history = useQuery({
    queryKey: queryKeys.hr.history(id),
    queryFn: () => fetchEmploymentHistory(id),
    enabled: id !== '',
  })

  const jobTitles = useQuery({
    queryKey: queryKeys.hr.jobTitles(true),
    queryFn: () => fetchJobTitles(true),
    staleTime: 5 * 60_000,
  })

  const back = (
    <Button icon={<ArrowLeftOutlined />} onClick={() => void navigate('/hr/persons')}>
      {t('common.back')}
    </Button>
  )

  if (person.isPending) {
    return (
      <>
        <PageHeader title={t('nav.hrPersons')} extra={back} />
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </>
    )
  }

  if (person.isError || !person.data) {
    return (
      <>
        <PageHeader title={t('nav.hrPersons')} extra={back} />
        <Card>
          <EmptyState
            variant="error"
            title={t('hr.person.notFound')}
            // ⚠️ Doiradan tashqaridagi xodim ham AYNAN shu yerga keladi
            // va 404 bo'ladi (`HrExceptionHandler` javadoc'i): 403
            // "bunday xodim BOR, lekin ko'rsatmaymiz" degan ma'lumotni
            // oshkor qilardi.
            description={translateError(person.error, t)}
          />
        </Card>
      </>
    )
  }

  const row = person.data
  const terminated = row.state === 'TERMINATED'

  return (
    <>
      <PageHeader
        title={row.fullName}
        description={t('pageIntro.hrPersonDetail')}
        extra={
          <Space wrap>
            {back}
            <Can permission="hr.person.write">
              <Button onClick={() => setEditOpen(true)}>{t('common.edit')}</Button>
            </Can>
            {terminated ? null : (
              <Can permission="hr.employment.write">
                <Button onClick={() => setAssignOpen(true)}>{t('hr.assignment.assignAction')}</Button>
              </Can>
            )}
          </Space>
        }
      />

      {runner.result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert
            partialNote={false}
            result={runner.result}
            closable
            onClose={runner.clear}
          />
        </div>
      ) : null}

      {/*
        ⚠️ VARAQLAR (tab) — bitta uzun ustun EMAS. Kartochkaga to'rtta
        yangi blok qo'shildi (maosh, varaqa, ta'til, bank) va ular bir
        ustunda tursa, kadr xodimi har safar maosh blokidan O'TIB
        tayinlash tarixiga tushardi. Varaqlar ularni AJRATADI: har
        varaq bitta savolga javob beradi.

        ⚠️ HUQUQI YO'Q VARAQ HAM CHIZILADI va bu ONGLI. Uni yashirish
        «bunday bo'lim yo'q» degan yolg'on taassurot berardi; ochilganda
        esa SABAB va yetishmayotgan huquq KODI ko'rinadi
        (`PermissionNotice`). So'rovning o'zi baribir yuborilmaydi —
        `enabled` har blokning ichida.
      */}
      <Tabs
        defaultActiveKey="general"
        items={[
          {
            key: 'general',
            label: t('hr.tab.general'),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card title={t('hr.person.sectionPersonal')}>
          <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered>
            <Descriptions.Item label={t('hr.person.personnelNo')}>
              {row.personnelNo}
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.person.state')}>
              <Space size={6} wrap>
                <Tag color={STATE_COLOR[row.state]}>{t(`personState.${row.state}`)}</Tag>
                {row.terminatedAt === null ? null : (
                  <Typography.Text type="secondary">
                    {t('hr.person.terminatedOn', { date: row.terminatedAt })}
                  </Typography.Text>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.person.lastName')}>{row.lastName}</Descriptions.Item>
            <Descriptions.Item label={t('hr.person.firstName')}>{row.firstName}</Descriptions.Item>
            <Descriptions.Item label={t('hr.person.middleName')}>
              {row.middleName ?? '—'}
            </Descriptions.Item>
            {/* ⚠️ `LocalDate` — xom holda. Zona konvertatsiyasi kunni siljitardi */}
            <Descriptions.Item label={t('hr.person.hiredAt')}>{row.hiredAt}</Descriptions.Item>
            <Descriptions.Item label={t('hr.person.phone')}>{row.phone ?? '—'}</Descriptions.Item>
            <Descriptions.Item label={t('hr.person.email')}>{row.email ?? '—'}</Descriptions.Item>
            {can('hr.person.document.view') ? (
              <Descriptions.Item label={t('hr.person.address')} span={2}>
                {row.address ?? '—'}
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item label={t('hr.person.emergency')} span={2}>
              {row.emergencyName === null && row.emergencyPhone === null
                ? t('hr.person.emergencyMissing')
                : `${row.emergencyName ?? '—'} · ${row.emergencyPhone ?? '—'}`}
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.person.updatedAt')} span={2}>
              {row.updatedAt === null ? (
                '—'
              ) : (
                <Tooltip title={utcTooltip(row.updatedAt)}>
                  <span>{formatDateTime(row.updatedAt)}</span>
                </Tooltip>
              )}
            </Descriptions.Item>
          </Descriptions>

          <Can permission="hr.person.write">
            <Space size="small" wrap style={{ marginTop: 16 }}>
              {terminated ? null : row.state === 'ACTIVE' ? (
                <Button
                  size="small"
                  loading={runner.running}
                  onClick={() => void runner.run(() => setPersonState(row.id, 'SUSPENDED'))}
                >
                  {t('hr.person.suspend')}
                </Button>
              ) : (
                <Button
                  size="small"
                  loading={runner.running}
                  onClick={() => void runner.run(() => setPersonState(row.id, 'ACTIVE'))}
                >
                  {t('hr.person.activate')}
                </Button>
              )}
              {terminated ? null : (
                <Button size="small" danger onClick={() => setTerminateOpen(true)}>
                  {t('hr.person.terminateAction')}
                </Button>
              )}
              {/*
                ⚠️ Ishdan bo'shaganni QAYTA ISHGA OLISH tugmasi YO'Q va
                bu ataylab: `POST /state` `TERMINATED` dan chiqishni
                qo'llab-quvvatlamaydi (`PersonServiceImpl` faqat
                `ACTIVE`/`SUSPENDED` ni qabul qiladi). Tugma chizib,
                serverdan rad javob olish operatorga nuqson bo'lib
                ko'rinardi. Qayta ishga olish — yangi kadr yozuvi.
              */}
              {terminated ? (
                <Typography.Text type="secondary">{t('hr.person.terminatedNote')}</Typography.Text>
              ) : null}
            </Space>
          </Can>
        </Card>

        <PersonAttachmentsCard person={row} />

        <Card
          title={t('hr.person.sectionCurrent')}
          extra={
            row.assignmentOpen ? null : (
              <Tag color="gold">{t('hr.person.assignmentClosed')}</Tag>
            )
          }
        >
          {row.assignmentOpen ? null : (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('hr.person.assignmentClosedHint')}
            />
          )}
          <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered>
            <Descriptions.Item label={t('hr.assignment.orgNode')}>
              {row.orgNodeName ?? row.orgNodeCode ?? '—'}
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.assignment.jobTitle')}>
              {row.jobTitleName
                ? seededName('jobTitles', row.jobTitleCode, row.jobTitleName, t)
                : (row.jobTitleCode ?? '—')}
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.assignment.employmentType')}>
              {/*
                ⚠️ `null` tekshiruvi QOLADI, `defaultValue` esa OLIB
                TASHLANDI — ular boshqa narsani anglatadi. `null` bu
                yerda "tayinlash umuman yo'q" (`LEFT JOIN LATERAL`,
                `PersonView.employmentType` javadoc'i). Tanilmagan
                QIYMAT esa endi mumkin emas: baza `CHECK`, server
                `enum`.
              */}
              {row.employmentType === null ? '—' : t(`employmentType.${row.employmentType}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('hr.assignment.validFrom')}>
              {row.assignmentFrom ?? '—'}
            </Descriptions.Item>
            {/*
              ⚠️ Yo'l ID SIFATIDA emas, faqat "kimning ostida" ni
              ko'rsatish uchun (`PersonView.orgNodePath` javadoc'i): u
              tugun ko'chirilganda O'ZGARADI, `orgNodeId` esa hech
              qachon.
            */}
            <Descriptions.Item label={t('hr.assignment.orgNodePath')} span={2}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.orgNodePath ?? '—'}
              </Typography.Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title={t('hr.history.title')}>
          <EmploymentHistory periods={history.data ?? []} loading={history.isPending} />
          {history.isError ? (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 16 }}
              message={translateError(history.error, t)}
            />
          ) : null}
        </Card>
              </Space>
            ),
          },
          {
            key: 'pay',
            label: t('hr.tab.pay'),
            children: (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <SalaryCard personId={row.id} />
                <PayslipsCard personId={row.id} />
              </Space>
            ),
          },
          {
            key: 'leave',
            label: t('hr.tab.leave'),
            children: <LeaveCard personId={row.id} />,
          },
          {
            key: 'bank',
            label: t('hr.tab.bank'),
            children: <BankAccountsCard personId={row.id} />,
          },
          {
            key: 'rating',
            label: t('hr.tab.rating'),
            children: (
              <RatingCard personId={row.id} hiredAt={row.hiredAt} terminatedAt={row.terminatedAt} />
            ),
          },
        ]}
      />

      <PersonFormModal
        open={editOpen}
        person={row}
        nodes={nodeSource.nodes}
        types={nodeSource.types}
        jobTitles={jobTitles.data ?? []}
        nodesReliable={nodeSource.reliable}
        onClose={() => setEditOpen(false)}
      />

      <EmploymentAssignModal
        open={assignOpen}
        person={row}
        nodes={nodeSource.nodes}
        types={nodeSource.types}
        jobTitles={jobTitles.data ?? []}
        nodesReliable={nodeSource.reliable}
        onClose={() => setAssignOpen(false)}
      />

      <PersonTerminateModal
        open={terminateOpen}
        person={row}
        submitting={runner.running}
        onClose={() => setTerminateOpen(false)}
        onSubmit={(terminatedAt, reason) => {
          void runner.run(() => terminatePerson(row.id, terminatedAt, reason)).then((result) => {
            // ⚠️ Faqat HAQIQIY xatoda oyna ochiq qoladi. `PARTIAL` da
            // yopiladi va natija sahifada ko'rinadi — amal BAJARILGAN,
            // oynani ochiq qoldirish "qayta bosing" degan yolg'on
            // taklif bo'lardi.
            if (result.status !== 'FAILED') setTerminateOpen(false)
          })
        }}
      />
    </>
  )
}
